import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { CATEGORY_COLORS, de, groupByCategory, type CategoryKey, type Item, type Receipt, type Store } from "@ainkauf/core";
import { Check, ChevronDown, ChevronLeft, Euro, PackageX, Store as StoreIcon, Undo2 } from "lucide-react";
import { SwipeRow } from "@/components/SwipeRow";
import { FinishSheet } from "@/components/FinishSheet";
import { MicButton } from "@/components/MicButton";
import { StoreSheet } from "@/components/StoreSheet";
import { SyncBadge } from "@/components/SyncBadge";
import {
  allMembers,
  allProducts,
  allStores,
  categoryOrderForList,
  finishTrip,
  itemsOfList,
  listById,
  setItemChecked,
  setItemUnavailable,
  startTrip,
  tripById,
  waehleLaden,
} from "@/db/repository";
import { kategorieZeichen } from "@/lib/category-icons";
import { useNavigate } from "@/lib/router";
import { activeUserId } from "@/lib/session";
import { useWakeLock } from "@/lib/wake-lock";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

type Gang = CategoryKey | "ohne_kategorie";

const gangFarbe = (key: Gang) => (key === "ohne_kategorie" ? "var(--ink-3)" : `var(--food-${CATEGORY_COLORS[key]})`);

/** Wie lange "Ben hat Milch im Wagen" stehen bleibt. */
const WAGEN_HINWEIS_MS = 2 * 60_000;

/**
 * Einkaufsmodus: ein Bildschirm für den Gang durch den Laden.
 *
 * Die erste Fassung zeigte alle Gänge untereinander, dazu zwei Hinweistexte,
 * einen Zähler und je Zeile zwei Bedienelemente. Im Laden, einhändig, mit
 * Wagen, war das zu viel Information für zu wenig Aufmerksamkeit.
 *
 * Vom 20.09. bis 17.10.2026 galt: ein Gang groß, die übrigen als Zeilen mit
 * ihrem Namen darunter. Auf dem Telefon waren so drei Artikel zu sehen, der
 * Rest steckte hinter Gangnamen. Daniel wollte es wie in Microsoft To Do: alles
 * auf einen Blick, dicht, und die Warengruppe nur als Zeichen vorn.
 *
 * Jetzt gilt: alle offenen Artikel in einer Liste, in Laufrichtung des Ladens
 * sortiert, ohne Überschriften. Etwas Luft trennt die Gänge. Alles Erklärende
 * ist weg, der Bildschirm bleibt an, unten liegt der Abschluss.
 *
 * Zu zweit im Laden: Was der andere abhakt, verschwindet hier, und eine
 * Zeile sagt kurz, wer was im Wagen hat. Innerhalb eines Gangs hängt sich ein
 * neuer Artikel unten an.
 */
export function ShoppingScreen({ listId }: { listId: string }) {
  const go = useNavigate();
  const [einkaufId, setEinkaufId] = useState<string | null>(null);
  const [fragtNachBetrag, setFragtNachBetrag] = useState(false);

  // Für "Ben hat Milch im Wagen": der Hinweis soll von selbst verschwinden,
  // auch wenn sich sonst nichts tut. Ein Takt alle 30 Sekunden lässt die
  // Abfrage neu laufen, und die bringt die Uhrzeit gleich mit. Im Render
  // selbst darf keine Uhr gelesen werden.
  const [takt, setTakt] = useState(0);
  useEffect(() => {
    const uhr = window.setInterval(() => {
      setTakt((n) => n + 1);
    }, 30_000);
    return () => {
      window.clearInterval(uhr);
    };
  }, []);

  const list = useLiveQuery(() => listById(listId), [listId], null);
  const geladen = useLiveQuery(
    async () => ({ items: await itemsOfList(listId), jetzt: Date.now() }),
    [listId, takt],
    { items: [] as Item[], jetzt: 0 },
  );
  const { items, jetzt } = geladen;
  const order = useLiveQuery(async () => categoryOrderForList(list), [list?.id, list?.store_id], [] as CategoryKey[]);
  const preise = useLiveQuery(
    async () => new Map((await allProducts()).map((product) => [product.id, product.last_price_cents ?? 0])),
    [],
    new Map<string, number>(),
  );
  const mitglieder = useLiveQuery(() => allMembers(), [], []);
  const laeden = useLiveQuery(() => allStores(), [], [] as Store[]);
  const [ladenBlatt, setLadenBlatt] = useState(false);
  const laden = laeden.find((eintrag: Store) => eintrag.id === list?.store_id) ?? null;
  // Der Einkauf als Live-Abfrage: Schließt ihn jemand anders ab, steht das
  // hier, sobald die Zeile über Realtime da ist.
  const einkauf = useLiveQuery(async () => (einkaufId ? tripById(einkaufId) : null), [einkaufId], null);

  useWakeLock(true);

  // Der Einkauf entsteht beim Betreten, nicht beim Abschließen: nur so kann
  // das andere Telefon melden, dass hier gerade jemand im Laden steht. Erst
  // wenn die Liste geladen ist, sonst entstünde ein Einkauf ohne Laden und
  // gleich danach ein zweiter mit.
  const begonnen = useRef(false);
  useEffect(() => {
    if (list === null || begonnen.current) return;
    begonnen.current = true;
    void startTrip(list.store_id).then((trip) => {
      setEinkaufId(trip.id);
    });
  }, [list]);

  const ich = activeUserId();
  const namen = new Map(mitglieder.map((mitglied) => [mitglied.user_id, mitglied.display_name]));

  const offen = items.filter((item) => !item.is_checked && item.unavailable_at === null);
  const erledigt = items.filter((item) => item.is_checked);
  const fehlt = items.filter((item) => !item.is_checked && item.unavailable_at !== null);

  /*
    Im Laden zählt Ruhe, nicht Neuheit: Die Zeilen eines Gangs stehen in der
    Reihenfolge, in der sie angelegt wurden, ältere oben. Was jemand gerade
    dazutippt, hängt sich am Ende seines Gangs an.
  */
  const gaenge = groupByCategory(offen, order).map((abschnitt) => ({ ...abschnitt, items: [...abschnitt.items].reverse() }));

  // Der jüngste fremde Haken, solange er frisch ist.
  const fremderHaken = erledigt
    .filter((item) => item.checked_by !== null && item.checked_by !== ich && item.checked_at !== null)
    .filter((item) => jetzt - Date.parse(item.checked_at ?? "") < WAGEN_HINWEIS_MS)
    .sort((a, b) => Date.parse(b.checked_at ?? "") - Date.parse(a.checked_at ?? ""))[0];

  const vonAnderemAbgeschlossen = einkauf !== null && einkauf.finished_at !== null;

  const summeCent = erledigt.reduce((total, item) => total + (item.product_id ? (preise.get(item.product_id) ?? 0) : 0), 0);

  const abschliessen = useCallback(
    async (totalCents: number | null, receipt: Receipt | null) => {
      // Hat jemand anders diesen Einkauf schon abgeschlossen, bleibt er
      // unangetastet, und was hier noch im Wagen liegt, wird ein eigener.
      const laufend = einkauf !== null && einkauf.finished_at === null ? einkauf : null;
      await finishTrip(listId, {
        storeId: list?.store_id ?? null,
        tripId: laufend?.id,
        startedAt: laufend?.started_at,
        startedBy: laufend?.started_by,
        totalCents,
        receipt,
      });
      go("/");
    },
    [einkauf, go, list?.store_id, listId],
  );

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col px-4 pb-32">
      <header className="glas sticky top-0 z-10 flex items-center justify-between gap-2 py-3">
        <button
          type="button"
          onClick={() => {
            go(`/liste/${listId}`);
          }}
          aria-label={de.trip.leave}
          data-testid="leave-shopping"
          className="inline-flex size-11 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
        >
          <ChevronLeft size={22} strokeWidth={2} aria-hidden />
        </button>

        <span className="flex min-w-0 items-center gap-2">
          <span className="num truncate text-sm font-semibold text-ink-2" data-testid="shopping-remaining">
            {de.trip.remaining(offen.length)}
          </span>
          {/* Im Keller des Supermarkts ist das Netz oft weg. Dann muss man
              sehen, dass die Haken warten, und nicht erst zu Hause. */}
          <SyncBadge />
        </span>

        {summeCent > 0 ? (
          <span className="num rounded-full bg-card px-3 py-2 text-sm font-semibold text-ink-2 shadow-[var(--shadow-card)]" data-testid="shopping-sum">
            {euro(summeCent)}
          </span>
        ) : (
          <span className="size-11" aria-hidden />
        )}

        {/* Fortschrittsband am unteren Rand der Kopfleiste: der einzige
            Hinweis, den man im Laden im Vorbeigehen braucht. */}
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 overflow-hidden rounded-full bg-paper-2">
          <span
            className="block h-full rounded-full"
            style={{
              width: `${String(items.length === 0 ? 0 : Math.round((erledigt.length / items.length) * 100))}%`,
              background: "linear-gradient(90deg, var(--accent-2), var(--accent))",
              transition: "width 420ms var(--ease-out)",
            }}
          />
        </span>
      </header>

      <main className="flex flex-1 flex-col gap-3 pt-2">
        {/* Wo man gerade steht, entscheidet über die Reihenfolge der Gänge.
            Freiwillig: Ohne Laden bleibt es bei der bisherigen Sortierung. */}
        <button
          type="button"
          onClick={() => {
            setLadenBlatt(true);
          }}
          data-testid="shopping-store"
          data-store={laden?.name ?? ""}
          className="inline-flex min-h-[44px] items-center gap-2 self-start rounded-full bg-card px-4 text-sm font-semibold text-ink-2 shadow-[var(--shadow-card)]"
        >
          <StoreIcon size={16} strokeWidth={1.75} aria-hidden />
          {laden === null ? de.trip.storeNone : laden.name}
          <ChevronDown size={15} strokeWidth={2} aria-hidden className="text-ink-3" />
        </button>

        {vonAnderemAbgeschlossen && (
          <p
            role="status"
            data-testid="trip-finished-by-other"
            className="rounded-[var(--radius-lg)] bg-honey-soft px-4 py-3 text-sm text-ink-2"
          >
            {de.trip.finishedElsewhere}
          </p>
        )}

        {fremderHaken !== undefined && (
          <p role="status" data-testid="in-cart-of" className="px-1 text-sm font-semibold text-herb">
            {de.trip.inCartOf(namen.get(fremderHaken.checked_by ?? "") ?? de.family.someone, fremderHaken.name)}
          </p>
        )}

        {gaenge.length === 0 ? (
          <p className="karte px-5 py-12 text-center text-lg text-ink-2">{de.lists.allDone}</p>
        ) : (
          /*
            Jeder Gang ist ein eigener Abschnitt, aber ohne Überschrift: Das
            Zeichen vorn in der Zeile trägt die Warengruppe. Der Abstand
            zwischen den Abschnitten ist größer als zwischen den Zeilen, das
            reicht, um die Gänge zu sehen.
          */
          gaenge.map((gang, g) => {
            const farbe = gangFarbe(gang.key);
            return (
              <section key={gang.key} data-testid="aisle" data-name={gang.label}>
                <ul className="flex flex-col gap-1.5">
                  {gang.items.map((item, i) => (
                    <li key={item.id} className="auftritt" style={{ ["--takt" as string]: `${String(Math.min((g * 3 + i) * 20, 240))}ms` }}>
                      <SwipeRow onSwipe={() => void setItemUnavailable(item.id, true)}>
                        <div className="karte kante flex items-center gap-1" style={{ ["--kante-farbe" as string]: farbe }}>
                          <button
                            type="button"
                            onClick={() => void setItemChecked(item.id, true)}
                            data-testid="shopping-item"
                            data-name={item.name}
                            className="flex min-h-[52px] min-w-0 flex-1 items-center gap-3 py-1 pl-3 pr-1 text-left text-[17px] transition-transform active:scale-[0.99]"
                          >
                            <span
                              aria-hidden
                              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border-2"
                              style={{
                                borderColor: `color-mix(in oklab, ${farbe} 55%, transparent)`,
                                background: `color-mix(in oklab, ${farbe} 12%, #fff)`,
                                color: farbe,
                              }}
                            >
                              {kategorieZeichen(gang.key, 17)}
                            </span>

                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{item.name}</span>
                              {/* Im Laden die wichtigste Zusatzangabe: nur mitnehmen,
                                  wenn der Preis stimmt. */}
                              {item.only_on_sale === true && (
                                <span data-testid="sale-badge" className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-honey-soft px-2 text-[12px] font-bold leading-5 text-ink">
                                  <Euro size={12} strokeWidth={2.5} aria-hidden />
                                  {de.items.onlyOnSale}
                                </span>
                              )}
                              {item.note !== null && <span className="block truncate text-[13px] leading-tight text-ink-2">{item.note}</span>}
                            </span>

                            {item.quantity !== null && (
                              <span
                                className="num shrink-0 rounded-full px-2.5 py-0.5 text-sm font-bold"
                                style={{
                                  background: `color-mix(in oklab, ${farbe} 15%, #fff)`,
                                  color: `color-mix(in oklab, ${farbe} 72%, var(--ink))`,
                                }}
                              >
                                {item.quantity}
                                {item.unit ? ` ${item.unit}` : ""}
                              </span>
                            )}
                          </button>

                          {/* Wischen geht auch, aber nichts darf allein daran
                              hängen (Regel 8). Deshalb dieser Knopf, leise. */}
                          <button
                            type="button"
                            onClick={() => void setItemUnavailable(item.id, true)}
                            aria-label={`${de.trip.unavailable}: ${item.name}`}
                            data-testid="mark-unavailable"
                            data-name={item.name}
                            className="mr-1 inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
                          >
                            <PackageX size={17} strokeWidth={1.75} aria-hidden />
                          </button>
                        </div>
                      </SwipeRow>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}

        {fehlt.length > 0 && (
          <section className="flex flex-col gap-1.5" data-testid="unavailable-section">
            <h2 className="px-1 text-[13px] font-bold uppercase tracking-[0.06em] text-honey">{de.trip.unavailable}</h2>
            <ul className="flex flex-col gap-1.5">
              {fehlt.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      void setItemUnavailable(item.id, false);
                    }}
                    data-testid="unavailable-item"
                    data-name={item.name}
                    className="flex min-h-[52px] w-full items-center gap-2 rounded-[var(--radius-lg)] bg-honey-soft px-4 text-left text-ink-2"
                  >
                    <Undo2 size={16} strokeWidth={2} className="shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <div className="fixed inset-x-0 z-20 px-4" style={{ bottom: "calc(12px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2">
        <MicButton currentListId={listId} gross />
        <button
          type="button"
          onClick={() => {
            setFragtNachBetrag(true);
          }}
          data-testid="shopping-finish"
          className="flex min-h-[60px] min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-herb text-lg font-bold text-white shadow-[var(--shadow-float)] transition-transform active:scale-[0.98]"
        >
          <Check size={20} strokeWidth={2.5} aria-hidden />
          {de.trip.finish}
        </button>
        </div>
      </div>

      {ladenBlatt && (
        <StoreSheet
          stores={laeden}
          activeStoreId={list?.store_id ?? null}
          onChoose={(wahl) => {
            setLadenBlatt(false);
            void waehleLaden(listId, einkaufId, wahl);
          }}
          onCancel={() => {
            setLadenBlatt(false);
          }}
        />
      )}

      {fragtNachBetrag && (
        <FinishSheet
          onFinish={abschliessen}
          onCancel={() => {
            setFragtNachBetrag(false);
          }}
        />
      )}
    </div>
  );
}
